/**
 * WebSocket Hook for Real-time Market Data
 * Manages WebSocket connections with automatic reconnection and error handling.
 *
 * Private channels use SOC2 short-lived tickets (mint on every connect/reconnect).
 * Public market-data may use a ticket when available, or the public path without one.
 */

'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useErrorContext } from '@/shared/components/error/ErrorContext';
import {
  connectSecureWs,
  isPrivateChannel,
  type WsChannel,
  WsTicketError,
} from '@/infrastructure/websocket/secureSocket';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface WebSocketConfig {
  /**
   * Static WebSocket URL (legacy / public).
   * Prefer `channel` + ticket flow for production private sockets.
   */
  url?: string;
  /**
   * Channel name for ticket-based secure WS (e.g. `orders`, `market-data`).
   * When set, a new ticket is minted on every connect/reconnect.
   */
  channel?: WsChannel;
  /**
   * Mint short-lived ticket before connect. Defaults to true when `channel` is set.
   */
  useTicket?: boolean;
  /**
   * Require a ticket. Defaults to true for private channels, false for public.
   */
  requireTicket?: boolean;
  /** Soft-fail on 401 (no login redirect). Defaults inverse of private channel. */
  softFailAuth?: boolean;
  /** Reconnection settings */
  reconnect?: {
    /** Whether to auto-reconnect */
    enabled: boolean;
    /** Maximum reconnection attempts */
    maxAttempts: number;
    /** Delay between attempts (ms) */
    delay: number;
    /** Exponential backoff multiplier */
    backoffMultiplier: number;
  };
  /** Heartbeat settings */
  heartbeat?: {
    /** Whether to send heartbeat */
    enabled: boolean;
    /** Heartbeat interval (ms) */
    interval: number;
    /** Heartbeat message */
    message: string | object;
  };
}

export interface WebSocketState {
  /** Connection status */
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  /** Last error message */
  error: string | null;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
  /** Connection timestamp */
  connectedAt: Date | null;
  /** Last message timestamp */
  lastMessageAt: Date | null;
}

export interface UseWebSocketReturn {
  /** Current connection state */
  state: WebSocketState;
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Send message to WebSocket */
  send: (message: string | object) => void;
  /** Connect to WebSocket */
  connect: () => void;
  /** Disconnect from WebSocket */
  disconnect: () => void;
  /** Subscribe to message type */
  subscribe: (messageType: string, handler: (data: any) => void) => () => void;
  /** Unsubscribe from message type */
  unsubscribe: (messageType: string) => void;
}

// =============================================================================
// DEFAULT CONFIGURATION
// =============================================================================

const defaultConfig: Required<
  Pick<WebSocketConfig, 'reconnect' | 'heartbeat'>
> &
  WebSocketConfig = {
  url: '',
  useTicket: true,
  reconnect: {
    enabled: true,
    maxAttempts: 5,
    delay: 1000,
    backoffMultiplier: 2,
  },
  heartbeat: {
    enabled: true,
    interval: 30000, // 30 seconds
    message: { type: 'ping' },
  },
};

// =============================================================================
// WEBSOCKET HOOK
// =============================================================================

export const useWebSocket = (
  config: WebSocketConfig,
  onMessage?: (data: any) => void
): UseWebSocketReturn => {
  const { reportError } = useErrorContext();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messageHandlersRef = useRef<Map<string, Set<(data: any) => void>>>(new Map());
  const connectGenerationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const intentionalCloseRef = useRef(false);
  const configRef = useRef(config);
  configRef.current = config;
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const [state, setState] = useState<WebSocketState>({
    status: 'disconnected',
    error: null,
    reconnectAttempts: 0,
    connectedAt: null,
    lastMessageAt: null,
  });

  const reconnectCfg = {
    ...defaultConfig.reconnect,
    ...config.reconnect,
  };
  const heartbeatCfg = {
    ...defaultConfig.heartbeat,
    ...config.heartbeat,
  };
  const reconnectCfgRef = useRef(reconnectCfg);
  reconnectCfgRef.current = reconnectCfg;
  const heartbeatCfgRef = useRef(heartbeatCfg);
  heartbeatCfgRef.current = heartbeatCfg;

  // Clear timers
  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Start heartbeat — read config from ref so object identity cannot churn connect()
  const startHeartbeat = useCallback(() => {
    const hb = heartbeatCfgRef.current;
    if (!hb.enabled) return;

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const current = heartbeatCfgRef.current;
        const message =
          typeof current.message === 'string'
            ? current.message
            : JSON.stringify(current.message);
        wsRef.current.send(message);
      }
    }, hb.interval);
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback((event: MessageEvent) => {
    setState((prev) => ({ ...prev, lastMessageAt: new Date() }));

    try {
      const data = JSON.parse(event.data as string);

      onMessageRef.current?.(data);

      if (data.type && messageHandlersRef.current.has(data.type)) {
        const handlers = messageHandlersRef.current.get(data.type);
        handlers?.forEach((handler) => handler(data));
      }
    } catch {
      console.warn('Failed to parse WebSocket message:', event.data);
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    const cfg = reconnectCfgRef.current;
    if (!cfg.enabled) return;

    setState((prev) => {
      if (prev.reconnectAttempts >= cfg.maxAttempts) {
        const error = 'Maximum reconnection attempts reached';
        reportError(new Error(error), {
          context: 'WebSocket Connection',
          severity: 'high',
        });
        return { ...prev, status: 'error', error };
      }

      const attempt = prev.reconnectAttempts + 1;
      const delay =
        cfg.delay * Math.pow(cfg.backoffMultiplier, prev.reconnectAttempts);

      reconnectTimeoutRef.current = setTimeout(() => {
        // connect is resolved via ref below to avoid stale closures
        connectRef.current?.();
      }, delay);

      return {
        ...prev,
        reconnectAttempts: attempt,
      };
    });
  }, [reportError]);

  const connectRef = useRef<(() => void) | null>(null);

  // Connect to WebSocket — mints a NEW ticket each call when channel/ticket mode is on
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const cfg = configRef.current;
    const channel = cfg.channel;
    const useTicket =
      cfg.useTicket !== false && Boolean(channel);
    const requireTicket =
      cfg.requireTicket ?? (channel ? isPrivateChannel(channel) : false);
    const softFailAuth =
      cfg.softFailAuth ?? (channel ? !isPrivateChannel(channel) : true);

    intentionalCloseRef.current = false;
    connectGenerationRef.current += 1;
    const generation = connectGenerationRef.current;

    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;

    setState((prev) => ({ ...prev, status: 'connecting', error: null }));
    clearTimers();

    const attachHandlers = (socket: WebSocket) => {
      if (generation !== connectGenerationRef.current) {
        socket.close(1000, 'Superseded connect');
        return;
      }

      wsRef.current = socket;

      socket.onopen = () => {
        if (generation !== connectGenerationRef.current) return;
        setState((prev) => ({
          ...prev,
          status: 'connected',
          error: null,
          reconnectAttempts: 0,
          connectedAt: new Date(),
        }));
        startHeartbeat();
      };

      socket.onmessage = handleMessage;

      socket.onclose = (event) => {
        if (generation !== connectGenerationRef.current) return;
        setState((prev) => ({
          ...prev,
          status: 'disconnected',
          connectedAt: null,
        }));
        clearTimers();
        wsRef.current = null;

        // Reconnect if enabled and not an intentional clean close
        if (
          reconnectCfgRef.current.enabled &&
          !intentionalCloseRef.current &&
          event.code !== 1000
        ) {
          scheduleReconnect();
        }
      };

      socket.onerror = () => {
        if (generation !== connectGenerationRef.current) return;
        const error = 'WebSocket connection error';
        setState((prev) => ({ ...prev, status: 'error', error }));
        reportError(new Error(error), {
          context: 'WebSocket Connection',
          severity: 'high',
        });
      };
    };

    const run = async () => {
      try {
        let socket: WebSocket;

        if (useTicket && channel) {
          // Mint NEW ticket then open immediately (never reuse)
          socket = await connectSecureWs({
            channel,
            requireTicket,
            softFailAuth,
            signal: abort.signal,
          });
        } else if (cfg.url) {
          socket = new WebSocket(cfg.url);
        } else {
          throw new Error(
            'WebSocket config requires either `channel` (ticket flow) or `url`'
          );
        }

        if (abort.signal.aborted || generation !== connectGenerationRef.current) {
          socket.close(1000, 'Connect aborted');
          return;
        }

        attachHandlers(socket);
      } catch (error) {
        if (abort.signal.aborted || generation !== connectGenerationRef.current) {
          return;
        }

        const isAuth =
          error instanceof WsTicketError && error.status === 401;
        const errorMessage = isAuth
          ? 'WebSocket authentication required'
          : error instanceof Error
            ? error.message
            : 'Failed to create WebSocket connection';

        if (!isAuth) {
          reportError(
            error instanceof Error ? error : new Error(errorMessage),
            { context: 'WebSocket Connection', severity: 'high' }
          );
        }

        // Non-auth failures: normal reconnect. Auth (401 mint): allow a few
        // delayed retries for post-login cookie race, then stop (avoid hammer).
        const reconnectEnabled = reconnectCfgRef.current.enabled;
        if (reconnectEnabled && !isAuth) {
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: errorMessage,
          }));
          scheduleReconnect();
        } else if (reconnectEnabled && isAuth) {
          setState((prev) => {
            // Max 2 auth retries (~ soft cookie-settle window)
            if (prev.reconnectAttempts >= 2) {
              return { ...prev, status: 'error', error: errorMessage };
            }
            const attempt = prev.reconnectAttempts + 1;
            const delay = 400 * attempt;
            reconnectTimeoutRef.current = setTimeout(() => {
              connectRef.current?.();
            }, delay);
            return {
              ...prev,
              reconnectAttempts: attempt,
              status: 'connecting',
              error: errorMessage,
            };
          });
        } else {
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: errorMessage,
          }));
        }
      }
    };

    void run();
  }, [clearTimers, startHeartbeat, handleMessage, reportError, scheduleReconnect]);

  connectRef.current = connect;

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    connectGenerationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    clearTimers();

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setState((prev) => ({
      ...prev,
      status: 'disconnected',
      connectedAt: null,
      reconnectAttempts: 0,
    }));
  }, [clearTimers]);

  // Send message
  const send = useCallback((message: string | object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const messageStr =
        typeof message === 'string' ? message : JSON.stringify(message);
      wsRef.current.send(messageStr);
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
    }
  }, []);

  // Subscribe to specific message types
  const subscribe = useCallback(
    (messageType: string, handler: (data: any) => void) => {
      if (!messageHandlersRef.current.has(messageType)) {
        messageHandlersRef.current.set(messageType, new Set());
      }

      const handlers = messageHandlersRef.current.get(messageType)!;
      handlers.add(handler);

      return () => {
        handlers.delete(handler);
        if (handlers.size === 0) {
          messageHandlersRef.current.delete(messageType);
        }
      };
    },
    []
  );

  // Unsubscribe from all handlers of a message type
  const unsubscribe = useCallback((messageType: string) => {
    messageHandlersRef.current.delete(messageType);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    state,
    isConnected: state.status === 'connected',
    send,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
  };
};

// =============================================================================
// MARKET DATA WEBSOCKET HOOK
// =============================================================================

export interface MarketDataWebSocketConfig {
  /** Base WebSocket URL (legacy; prefer env NEXT_PUBLIC_WS_BASE_URL) */
  baseUrl?: string;
  /**
   * @deprecated Do not put API keys in the WebSocket query string.
   * Private/authenticated streams use short-lived tickets instead.
   */
  apiKey?: string;
  /** Symbols to subscribe to */
  symbols?: string[];
  /** Data types to subscribe to */
  dataTypes?: Array<'ticker' | 'orderbook' | 'trades' | 'ohlcv'>;
}

export const useMarketDataWebSocket = (
  config: MarketDataWebSocketConfig = {}
) => {
  const { symbols = [], dataTypes = ['ticker'] } = config;

  // apiKey intentionally ignored — never attach to WS URL (SOC2)
  if (process.env.NODE_ENV === 'development' && config.apiKey) {
    console.warn(
      '[useMarketDataWebSocket] apiKey is ignored; use ticket-based auth via /api/auth/ws-ticket'
    );
  }

  const [subscriptions, setSubscriptions] = useState<Set<string>>(new Set());
  const [marketData, setMarketData] = useState<Map<string, any>>(new Map());

  // Public market-data: ticket optional; no long-lived secrets in URL
  const wsConfig: WebSocketConfig = {
    channel: 'market-data',
    useTicket: true,
    requireTicket: false,
    softFailAuth: true,
    reconnect: {
      enabled: true,
      maxAttempts: 10,
      delay: 1000,
      backoffMultiplier: 1.5,
    },
    heartbeat: {
      enabled: true,
      interval: 30000,
      message: { type: 'ping' },
    },
  };

  // Handle incoming market data
  const handleMarketData = useCallback((data: any) => {
    if (data.type === 'pong') return; // Ignore pong responses

    setMarketData((prev) => {
      const updated = new Map(prev);
      const key = `${data.symbol}_${data.type}`;
      updated.set(key, { ...data, timestamp: Date.now() });
      return updated;
    });
  }, []);

  const { state, send, connect, disconnect, subscribe } = useWebSocket(
    wsConfig,
    handleMarketData
  );

  // Subscribe to symbol and data type
  const subscribeToSymbol = useCallback(
    (symbol: string, dataType: string) => {
      const subscriptionKey = `${symbol}_${dataType}`;

      if (!subscriptions.has(subscriptionKey)) {
        send({
          type: 'subscribe',
          symbol,
          dataType,
        });

        setSubscriptions((prev) => new Set(prev).add(subscriptionKey));
      }
    },
    [send, subscriptions]
  );

  // Unsubscribe from symbol and data type
  const unsubscribeFromSymbol = useCallback(
    (symbol: string, dataType: string) => {
      const subscriptionKey = `${symbol}_${dataType}`;

      if (subscriptions.has(subscriptionKey)) {
        send({
          type: 'unsubscribe',
          symbol,
          dataType,
        });

        setSubscriptions((prev) => {
          const updated = new Set(prev);
          updated.delete(subscriptionKey);
          return updated;
        });
      }
    },
    [send, subscriptions]
  );

  // Auto-subscribe to configured symbols and data types
  useEffect(() => {
    if (state.status === 'connected') {
      symbols.forEach((symbol) => {
        dataTypes.forEach((dataType) => {
          subscribeToSymbol(symbol, dataType);
        });
      });
    }
  }, [state.status, symbols, dataTypes, subscribeToSymbol]);

  // Get market data for specific symbol and type
  const getMarketData = useCallback(
    (symbol: string, dataType: string) => {
      const key = `${symbol}_${dataType}`;
      return marketData.get(key);
    },
    [marketData]
  );

  return {
    state,
    connect,
    disconnect,
    subscribeToSymbol,
    unsubscribeFromSymbol,
    getMarketData,
    marketData: Object.fromEntries(marketData),
    subscriptions: Array.from(subscriptions),
  };
};
